/**
 * Client HTTP.
 *
 * Un errore dell'API arriva già con un messaggio scritto per essere letto da
 * chi usa il programma: qui non si riscrive, si propaga. Inventare un «errore
 * imprevisto» al posto di «la fattura è già presente» è come togliere
 * l'unica informazione utile.
 */
export class ErroreApi extends Error {
  constructor(
    readonly stato: number,
    messaggio: string,
    readonly dettaglio?: unknown,
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }
}

async function richiesta<T>(percorso: string, opzioni: RequestInit = {}): Promise<T> {
  let risposta: Response;
  try {
    // Il `content-type: application/json` si dichiara **solo** quando c'è
    // davvero un corpo JSON. Dichiararlo su una richiesta senza body (una DELETE
    // o una POST senza payload) fa rispondere a Fastify «Body cannot be empty
    // when content-type is set to application/json» — un 400 che non c'entra
    // nulla con l'operazione. Il multipart (FormData) porta il proprio header.
    const haBodyJson = opzioni.body !== undefined && opzioni.body !== null && !(opzioni.body instanceof FormData);
    risposta = await fetch(percorso, {
      credentials: 'same-origin',
      ...opzioni,
      headers:
        opzioni.body instanceof FormData
          ? (opzioni.headers as HeadersInit)
          : haBodyJson
            ? { 'content-type': 'application/json', ...(opzioni.headers as Record<string, string>) }
            : (opzioni.headers as HeadersInit),
    });
  } catch {
    throw new ErroreApi(0, 'Impossibile contattare il server. Controlla che sia avviato e riprova.');
  }

  const testo = await risposta.text();
  let corpo: unknown = null;
  if (testo) {
    try {
      corpo = JSON.parse(testo);
    } catch {
      corpo = testo;
    }
  }

  if (!risposta.ok) {
    const c = corpo as { errore?: string; dettaglio?: unknown } | null;
    throw new ErroreApi(risposta.status, c?.errore ?? `Errore ${risposta.status}`, c?.dettaglio);
  }
  return corpo as T;
}

export const api = {
  get: <T>(p: string) => richiesta<T>(p),
  post: <T>(p: string, corpo?: unknown) =>
    richiesta<T>(p, { method: 'POST', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  put: <T>(p: string, corpo?: unknown) =>
    richiesta<T>(p, { method: 'PUT', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  del: <T>(p: string, corpo?: unknown) =>
    richiesta<T>(p, { method: 'DELETE', body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  carica: <T>(p: string, form: FormData) => richiesta<T>(p, { method: 'POST', body: form }),
};

// ── Tipi condivisi con l'API ────────────────────────────────────────────────

export interface Organizzazione {
  id: string;
  ragioneSociale: string;
  partitaIva: string | null;
  codiceFiscale: string | null;
  indirizzo: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  codiceDestinatario: string | null;
  pec: string | null;
  sogliaMinimaCent: number;
  formatoData: 'gg/mm/aaaa' | 'gg-mm-aaaa' | 'aaaa-mm-gg';
  regimeIva: 'MENSILE' | 'TRIMESTRALE';
  interessiIvaPercentuale: string;
  creditoIvaInizialeCent: number;
  categoriaIvaId: string | null;
  /** Se c'è un logo caricato. I byte non arrivano qui: si scaricano a parte. */
  logoPresente: boolean;
  logoAggiornatoIl: string | null;
  categoriaDefaultAttiveId: string | null;
  categoriaDefaultPassiveId: string | null;
  primoMeseEsercizio: number;
  aliquotaIvaDefault: string;
  /** Se il Budget suggerisce voci dai fornitori e clienti che si ripetono. */
  suggerisciRicorrenti: boolean;
}

export type { Tema } from '@mastro/core/tipi';

export interface Utente {
  id: string;
  email: string;
  nome: string | null;
  ruolo: 'ADMIN' | 'OPERATORE';
  tema: import('@mastro/core/tipi').Tema;
  /** Vero finché usa la password che gli ha dato l'amministratore. */
  deveCambiarePassword?: boolean;
  /**
   * Può aprire il registro di sistema. Non è un ruolo: lo decide
   * `MASTRO_SUPERADMIN` sul server, così chi amministra l'azienda non può
   * darselo da solo. Qui serve solo a decidere se mostrare la voce di menu.
   */
  superadmin?: boolean;
}

export interface UtenteGestito {
  id: string;
  email: string;
  nome: string | null;
  ruolo: 'ADMIN' | 'OPERATORE';
  attivo: boolean;
  deveCambiarePassword: boolean;
  ultimoAccesso: string | null;
  creatoIl: string;
}

export interface Categoria {
  id: string;
  padreId: string | null;
  nome: string;
  verso: 'ENTRATA' | 'USCITA';
  sistema: boolean;
  ordine: number;
}

export interface Anagrafica {
  id: string;
  denominazione: string;
  partitaIva: string | null;
  codiceFiscale: string | null;
  tipo: 'CLIENTE' | 'FORNITORE' | 'ENTRAMBI';
  indirizzo: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  pec: string | null;
  codiceDestinatario: string | null;
  categoriaId: string | null;
  ritardoMedioGiorni: number;
  /** Se lo mantiene Mastro dai pagamenti osservati o lo tiene fermo una persona. */
  ritardoAutomatico: boolean;
  note: string | null;
  statistichePagamento?: { pagate: number; ritardo: number | null };
  /** Su quante fatture chiuse è misurato il ritardo (solo nell'elenco). */
  fatturePerRitardo?: number;
  /** Quante ne servono perché la misura valga. */
  minimoPerRitardo?: number;
}

export interface Conto {
  /** Quanto la banca lascia andare sotto zero su questo conto. */
  fidoCent: number;
  id: string;
  nome: string;
  tipo: 'BANCA' | 'CASSA' | 'CARTA' | 'ANTICIPO_FATTURE';
  iban: string | null;
  banca: string | null;
  saldoInizialeCent: number;
  saldoCorrenteCent: number;
  dataUltimoImport: string | null;
  attivo: boolean;
  ordine: number;
  contribuisceAllaCassa: boolean;
}

/** L'elenco arriva troncato al `limite`: `totale` dice quanti sarebbero. */
export interface RispostaMovimenti {
  movimenti: Movimento[];
  totale: number;
  limite: number;
  scarto: number;
}

export interface Movimento {
  id: string;
  contoId: string;
  data: string;
  importoCent: number;
  dicitura: string;
  categoriaId: string | null;
  deciso: boolean;
  escluso: boolean;
  allocatoCent: number;
  residuoCent: number;
  conto: { id: string; nome: string; tipo: string };
  categoria: { id: string; nome: string; verso: string } | null;
  /** Da quale regola arriva la proposta, quando è automatica. */
  regola: { id: string; dicitura: string } | null;
  /**
   * La voce non è del movimento: arriva dalla fattura che sta pagando. Va
   * distinta a schermo, perché nessuno l'ha scelta per questo movimento.
   */
  categoriaEreditata?: boolean;
  /**
   * Dichiarato «visto»: la riconciliazione è finita anche senza un documento.
   * Dietro una commissione o un F24 la fattura non arriverà mai, e senza questo
   * il movimento resterebbe fra le cose da fare per sempre.
   */
  senzaDocumentoIl?: string | null;
}

export interface RigaDocumento {
  id?: string;
  numeroLinea: number;
  descrizione: string;
  quantita: string | null;
  unitaMisura: string | null;
  prezzoUnitarioCent: number;
  scontoPercentuale: string | null;
  prezzoTotaleCent: number;
  aliquotaIva: string;
  natura: string | null;
}

export interface ScadenzaDocumento {
  id: string;
  dataScadenza: string;
  dataCassaAttesa: string;
  importoCent: number;
  modalitaPagamento: string | null;
  statoOrigineEsterna: string | null;
  dataPagamentoEsterna: string | null;
  /** Un pagamento indica questa rata: non si può togliere senza staccarlo. */
  abbinata?: boolean;
}

/**
 * Una riga del registro. Il nome dell'autore è **copiato** dentro l'evento: se
 * la persona lascia l'azienda, un registro che perde l'autore perde il suo
 * unico scopo.
 */
export interface EventoRegistro {
  id: string;
  azione: string;
  nota: string;
  dettaglio: unknown;
  utenteNome: string;
  creatoIl: string;
}

/**
 * Una compensazione vista dal documento su cui insiste. Non è un pagamento:
 * `controDocumentoNumero` è la fattura di verso opposto che l'ha chiusa, e in
 * banca non è passato niente.
 */
export interface CompensazioneSuDocumento {
  id: string;
  importoCent: number;
  data: string;
  nota: string;
  utenteNome: string;
  creatoIl: string;
  controDocumentoId: string;
  controDocumentoNumero: string;
  controDocumentoTipo: 'ATTIVA' | 'PASSIVA' | null;
}

/** Un documento con cui se ne può compensare un altro: verso opposto, stessa P.IVA, ancora aperto. */
export interface DocumentoCompensabile {
  id: string;
  numero: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  /** Rovescia il verso: una emessa è un nostro debito, una ricevuta un credito. */
  notaDiCredito: boolean;
  dataCompetenza: string;
  dataCassaAttesa: string;
  totaleCent: number;
  residuoCent: number;
  controparte: string | null;
  partitaIva: string | null;
}

export interface Documento {
  id: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  numero: string;
  anagraficaId: string | null;
  dataCompetenza: string;
  dataScadenzaNominale: string | null;
  dataCassaAttesa: string;
  dataCassaEffettiva: string | null;
  imponibileCent: number;
  ivaCent: number;
  totaleCent: number;
  /** Ritenuta d'acconto: trattenuta dal cliente, non arriverà mai in banca. */
  ritenutaCent: number;
  allocatoCent: number;
  residuoCent: number;
  stato: 'DA_PAGARE' | 'PARZIALE' | 'PAGATA';
  statoEsteso: 'DA_PAGARE' | 'PARZIALE' | 'PAGATA' | 'SCADUTA';
  statoOrigineEsterna: string | null;
  dataPagamentoEsterna: string | null;
  origine: string;
  notaDiCredito: boolean;
  tipoDocumentoSdi: string;
  causale: string | null;
  categoriaId: string | null;
  controparte: { id: string; denominazione: string; partitaIva: string | null } | null;
  righe?: RigaDocumento[];
  riepiloghiIva?: { aliquotaIva: string; imponibileCent: number; impostaCent: number; natura: string | null }[];
  scadenze?: ScadenzaDocumento[];
  /** Le correzioni fatte a mano su questo documento: chi, quando e perché. */
  storia?: EventoRegistro[];
  abbinamenti?: { movimentoId: string; importoCent: number; data: string; dicitura: string; conto: string }[];
  /** Chiuse senza che in banca sia passato niente: stanno accanto agli abbinamenti, non al loro posto. */
  compensazioni?: CompensazioneSuDocumento[];
  haXmlOriginale?: boolean;
}

/**
 * Da dove viene un contributo al previsionale. `PIANO` è la stima (budget e
 * ricorrenti), `SCADUTI`
 * sono i documenti aperti con la data di cassa già passata, che il periodo in
 * corso si trascina dietro. Sta a sé e non dentro `DOCUMENTI` perché chi
 * controlla deve poter distinguere un arretrato da una scadenza del periodo:
 * confusi insieme, un credito fermo da mesi sembrerebbe in regola.
 */
export type Fonte = 'MOVIMENTI' | 'DOCUMENTI' | 'PIANO' | 'F24' | 'SCADUTI';

export interface PeriodoPrevisione {
  chiave: string;
  etichetta: string;
  inizio: string;
  fine: string;
  collocazione: 'PASSATO' | 'CORRENTE' | 'FUTURO';
  /** La cassa con cui il periodo apre. `null` dove la storia non arriva. */
  saldoInizialeCent: number | null;
  entrateCent: number;
  usciteCent: number;
  flussoNettoCent: number;
  entrateResidueCent: number;
  usciteResidueCent: number;
  flussoResiduoCent: number;
  /** Giroconti e movimenti esclusi: spostano la cassa ma non il prospetto. */
  movimentiEsclusiCent: number;
  saldoFinaleCent: number | null;
  inAllerta: boolean;
  perFonte: Record<Fonte, { entrateCent: number; usciteCent: number }>;
  contributi: { categoriaId: string | null; verso: 'ENTRATA' | 'USCITA'; fonte: Fonte; importoCent: number }[];
}

export interface Previsione {
  /** Documenti aperti con data di cassa già passata: si trascinano nel periodo in corso. */
  scaduti: { entrateCent: number; usciteCent: number; quantita: number };
  /** Fido complessivo: sotto `−fido` la banca non segue più. */
  fidoCent: number;
  /** L'ultimo movimento registrato in banca: serve a spiegare una colonna vuota. */
  ultimoMovimentoIl: string | null;
  cassaAttualeCent: number;
  fuoriCassaCent: number;
  sogliaMinimaCent: number;
  periodi: PeriodoPrevisione[];
  primaAllerta: PeriodoPrevisione | null;
  /** La cassa giorno per giorno, da oggi alla fine dell'orizzonte. */
  cammino: { data: string; saldoCent: number; minimoCent: number }[];
  /** Il punto più basso che la cassa tocca nell'orizzonte, dentro i mesi e non solo a fine mese. */
  minimoDiCassa: { data: string; saldoCent: number } | null;
  /** Il primo giorno in cui la cassa prevista scende sotto la soglia: da lì bisogna agire. */
  primoGiornoSottoSoglia: { data: string; saldoCent: number } | null;
  /** Le uscite che scavano il punto più basso e gli incassi che arrivano subito dopo. */
  causeDelMinimo: {
    uscite: { data: string; importoCent: number; etichetta?: string }[];
    entrateSubitoDopo: { data: string; importoCent: number; etichetta?: string }[];
  } | null;
  granularita: 'MESE' | 'SETTIMANA';
  orizzonte: number;
  categorie?: Categoria[];
}

/**
 * Da cosa è composta una cifra del previsionale: le righe che la formano,
 * divise per fonte. Serve alla verifica — davanti a 4.500 € la domanda è «da
 * dove escono?», e senza le righe l'unica risposta possibile è fidarsi.
 *
 * `totali.scadutiCent` è la quota di `documenti` con la data di cassa già
 * passata: è **dentro** `documentiCent`, non in aggiunta.
 */
export interface DettaglioPrevisionale {
  periodo: {
    chiave: string;
    etichetta: string;
    inizio: string;
    fine: string;
    collocazione: 'PASSATO' | 'CORRENTE' | 'FUTURO';
  };
  /** Nullo quando la cifra raccoglie ciò che non ha categoria. */
  categoria: { id: string; nome: string } | null;
  movimenti: { id: string; data: string; dicitura: string; importoCent: number; contoId: string }[];
  documenti: {
    id: string;
    numero: string;
    tipo: 'ATTIVA' | 'PASSIVA';
    controparte: string | null;
    /** Come sta sul documento. */
    dataScadenza: string;
    /** La stessa, corretta col ritardo osservato della controparte. */
    dataCassaAttesa: string;
    residuoCent: number;
    scaduto: boolean;
  }[];
  f24: { id: string; descrizione: string; dataScadenza: string; saldoCent: number }[];
  /** Budget e ricorrenti. Il loro totale è `totali.pianoCent`, non la loro somma. */
  piano: {
    etichetta: string;
    fonte: 'BUDGET' | 'RICORRENTE';
    importoCent: number;
    /** Per la stima di una controparte: quanto ne coprono le sue fatture già arrivate nel periodo. */
    copertoCent?: number;
    fattureCheCoprono?: string[];
  }[];
  totali: {
    movimentiCent: number;
    documentiCent: number;
    scadutiCent: number;
    f24Cent: number;
    /**
     * Non è la somma delle righe di piano: nel periodo già cominciato il piano
     * pesa solo per quanto eccede ciò che è già successo, e quella compensazione
     * riguarda la voce nel suo insieme.
     */
    pianoCent: number;
  };
}

/** Un insieme di ipotesi con cui proiettare il previsionale. */
export interface Scenario {
  id: string;
  nome: string;
  note: string | null;
  giorniIncasso: number;
  giorniPagamento: number;
  /** `numeric` viaggia come stringa: va convertito prima di farci i conti. */
  quotaRicavi: string;
  quotaCosti: string;
  quotaInsoluto: string;
}

/** Un fatto che chiede di fare qualcosa, con la sua cifra. */
export interface Segnale {
  chiave: string;
  gravita: 'ATTENZIONE' | 'NOTA';
  titolo: string;
  testo: string;
  dove?: string;
  valoreCent?: number;
}

/** Una riga di budget così com'è scritta, non come la mostra il prospetto. */
export interface VoceBudget {
  id: string;
  categoriaId: string;
  mese: string;
  importoCent: number;
}

export interface Andamento {
  dal: string;
  al: string;
  mesi: {
    chiave: string;
    etichetta: string;
    ricaviCent: number;
    costiCent: number;
    ebitdaCent: number;
    margineEbitda: number | null;
    ricaviCassaCent: number;
    costiCassaCent: number;
    ebitdaCassaCent: number;
  }[];
  competenza: { ricaviCent: number; costiCent: number; ebitdaCent: number; margineEbitda: number | null };
  cassa: { ricaviCent: number; costiCent: number; ebitdaCent: number };
  /** Competenza meno cassa: lavoro fatto che non è ancora diventato denaro. */
  scartoCent: number;
  tesoreria: { liquiditaCent: number; usciteMensiliCent: number; mesiDiAutonomia: number | null };
  pareggio: {
    costiFissiCent: number;
    costiVariabiliCent: number;
    quotaVariabile: number | null;
    puntoDiPareggioCent: number | null;
    margineDiSicurezzaCent: number | null;
    costiNonClassificatiCent: number;
  };
  /** Quanto ci si può fidare dei numeri qui sopra. */
  affidabilita: {
    categorieDaVerificare: { id: string; nome: string; verso: string }[];
    costiSuVocePredefinitaCent: number;
    costiTotaliCent: number;
    fornitori: number;
    fornitoriConVoce: number;
    movimentiSenzaCategoria: number;
    movimentiSenzaCategoriaCent: number;
    fattureConGemelloInBanca: number;
    fattureConGemelloCent: number;
  };
}

/** Di quello che c'è in banca, quanto non è dell'azienda. Indicativo: vedi `@mastro/core/iva`. */
export type IvaCruscotto = { f24DaVersareCent: number; cassaLiberaCent: number } & (
  | { stato: 'SENZA_F24_IVA' }
  | {
      stato: 'CALCOLATA';
      ultimoPeriodoCoperto: { dal: string; al: string };
      dal: string;
      al: string;
      debitoCent: number;
      creditoCent: number;
      nettoCent: number;
      daAccantonareCent: number;
      documenti: number;
    }
);

export interface Dashboard {
  iva: IvaCruscotto;
  previsione: Previsione;
  conti: Conto[];
  kpi: {
    da_incassare: number;
    da_pagare: number;
    scaduto_attivo: number;
    scaduto_passivo: number;
    n_scadute: number;
    movimentiDaSanare: number;
  };
  prossimeScadenze: {
    documentoId: string;
    tipo: 'ATTIVA' | 'PASSIVA';
    numero: string;
    dataCassaAttesa: string;
    importoCent: number;
    controparte: string | null;
  }[];
}

/** Una riga dello scadenzario: la rata di un documento non ancora saldato. */
export interface RigaScadenzario {
  documentoId: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  numero: string;
  /** Scadenza corretta col ritardo medio storico della controparte. */
  dataCassaAttesa: string;
  /** Scadenza nominale, come sta sul documento. */
  dataScadenza: string;
  /** Importo della rata (lo scadenzato programmato), in centesimi. */
  importoCent: number;
  /** Residuo dell'intero documento, in centesimi. */
  residuoDocumentoCent: number;
  stato: 'DA_PAGARE' | 'PARZIALE' | 'PAGATA';
  controparte: string | null;
  statoEsteso: 'DA_PAGARE' | 'PARZIALE' | 'PAGATA' | 'SCADUTA';
}
